import { CommonModule } from '@angular/common';
import {
  Component,
  input,
  AfterViewInit,
  ViewChild,
  ElementRef,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PDFDocumentProxy, PdfViewerModule } from 'ng2-pdf-viewer';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { TranslateModule } from '@ngx-translate/core';

const ROTATION_STEP = 90;
const ROTATION_LIMIT = 360;

@Component({
  selector: 'app-invoice-viewer',
  templateUrl: './invoice-viewer.component.html',
  styleUrl: './invoice-viewer.component.css',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PdfViewerModule,
    ButtonModule,
    InputNumberModule,
    TranslateModule,
  ],
})
export class InvoiceViewerComponent implements AfterViewInit, OnDestroy {
  fileSrc = input.required<string>();

  zoomPercent = 100;
  rotation = 0;
  page = 1;
  totalPages = 0;
  renderedPages = 0;

  private observer!: IntersectionObserver;
  private _lastScrollTop: number | null = null;

  @ViewChild('viewerWrapper', { static: true })
  viewerWrapper!: ElementRef<HTMLDivElement>;

  constructor(private readonly zone: NgZone) {}

  ngAfterViewInit(): void {
    this.observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        let maxRatio = 0;
        let visiblePage = this.page;

        for (const entry of entries) {
          const pageNum = Number((entry.target as HTMLElement).getAttribute('data-page-number'));
          if (entry.intersectionRatio > maxRatio) {
            maxRatio = entry.intersectionRatio;
            visiblePage = pageNum;
          }
        }

        this.zone.run(() => {
          if (this.page !== visiblePage) {
            this.page = visiblePage;
          }
        });
      },
      {
        root: this.viewerWrapper.nativeElement,
        threshold: this.buildThresholdList(),
      },
    );
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  onPdfLoadComplete(pdf: PDFDocumentProxy): void {
    this.totalPages = pdf.numPages;
    this.renderedPages = 0;
  }

  onPageRendered(): void {
    this.renderedPages++;
    if (this.renderedPages === this.totalPages) {
      setTimeout(() => {
        const pages = this.viewerWrapper.nativeElement.querySelectorAll('.pdf-viewer .page');

        pages.forEach(page => {
          if (page instanceof HTMLElement) {
            this.observer.observe(page);
          }
        });

        if (this._lastScrollTop != null) {
          this.viewerWrapper.nativeElement.scrollTop = this._lastScrollTop;
          this._lastScrollTop = null;
        }
      }, 300);
    }
  }

  onPageBlur(): void {
    if (this.page < 1) this.page = 1;
    else if (this.page > this.totalPages) this.page = this.totalPages;

    setTimeout(() => {
      const pageEl = this.viewerWrapper.nativeElement.querySelector(
        `.pdf-viewer .page[data-page-number="${this.page}"]`,
      ) as HTMLElement;
      pageEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
  }

  zoomIn(): void {
    this.rememberCurrentScrollPosition();
    if (this.zoomPercent < 300) this.zoomPercent += 25;
  }

  zoomOut(): void {
    this.rememberCurrentScrollPosition();
    if (this.zoomPercent > 25) this.zoomPercent -= 25;
  }

  private rememberCurrentScrollPosition(): void {
    const pageEl = this.viewerWrapper.nativeElement.querySelector(
      `.pdf-viewer .page[data-page-number="${this.page}"]`,
    ) as HTMLElement;

    if (pageEl) {
      this._lastScrollTop = pageEl.offsetTop;
    }
  }

  rotateRight(): void {
    this._updateRotation(this.rotation + ROTATION_STEP);
  }

  rotateLeft(): void {
    this._updateRotation(this.rotation - ROTATION_STEP);
  }

  public _updateRotation(newRotationValue: number): void {
    this.rotation = Math.abs(newRotationValue) >= ROTATION_LIMIT ? 0 : newRotationValue;
  }

  printPdf(): void {
    const url: string = this.fileSrc();
    const newTab = window.open(url, '_blank');
    if (newTab) newTab.onload = () => newTab.print();
    else console.error('Failed to open new tab for printing.');
  }

  downloadPdf(): void {
    const url: string = this.fileSrc();

    let fileName = 'invoice.pdf';
    const lastSlashIndex = url.lastIndexOf('/');
    const questionMarkIndex = url.indexOf('?', lastSlashIndex);

    if (lastSlashIndex !== -1) {
      fileName = url.substring(
        lastSlashIndex + 1,
        questionMarkIndex !== -1 ? questionMarkIndex : undefined,
      );
    }

    fetch(url)
      .then((response: Response) => response.blob())
      .then((blob: Blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fileName || 'invoice.pdf';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(error => {
        console.error('Error downloading PDF:', error);
      });
  }

  get zoom(): number {
    return this.zoomPercent / 100;
  }

  private buildThresholdList(): number[] {
    const thresholds: number[] = [];
    const numSteps = 20;

    for (let i = 0; i <= numSteps; i++) {
      thresholds.push(i / numSteps);
    }

    return thresholds;
  }
}
